import { useState } from "react";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import { useUpdateTopic, useMergeTopic } from "@/api/useGetTopics";
import { Button, Flex, Table, Text } from "@radix-ui/themes";
import { Topic } from "@/v2/entities/topic/Topic.model";
import {
  DotsVerticalIcon,
  Pencil1Icon,
  ArrowRightIcon,
} from "@radix-ui/react-icons";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import SharedSelect from "@/components/shared/select";
import { Loader2Icon } from "lucide-react";
import { toast } from "sonner";
import { ExpandableText } from "@/v2/components/expandableText/ExpandableText";

interface TopicItemProps {
  topic: Topic;
  refetch: () => void;
  allTopics: Topic[];
}

export function TopicItem({ topic, refetch, allTopics }: TopicItemProps) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [editTopicName, setEditTopicName] = useState(topic.topic || "");
  const [editDescription, setEditDescription] = useState(
    topic.description || ""
  );
  const [selectedToTopicId, setSelectedToTopicId] = useState<string>("");
  const { mutate: updateTopic, isLoading } = useUpdateTopic(topic.id);
  const { mutate: mergeTopic, isLoading: isMerging } = useMergeTopic();

  const handleEdit = () => {
    setEditTopicName(topic.topic || "");
    setEditDescription(topic.description || "");
    setEditDialogOpen(true);
  };

  const handleMerge = () => {
    setSelectedToTopicId("");
    setMergeDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editTopicName.trim()) return;

    await updateTopic(
      {
        id: topic.id,
        topic: editTopicName.trim(),
        description: editDescription.trim() || undefined,
      },
      {
        onSuccess: () => {
          refetch();
          setEditDialogOpen(false);
          toast("Topic updated successfully");
        },
        onError: () => {
          try {
            // window.quack?.proactiveTrigger();
          } catch (e) {}
          toast.error("Failed to update topic");
        },
      }
    );
  };

  const handleMergeConfirm = async () => {
    if (!selectedToTopicId || selectedToTopicId === topic.id.toString()) return;

    await mergeTopic(
      {
        fromTopicId: topic.id,
        toTopicId: parseInt(selectedToTopicId),
      },
      {
        onSuccess: () => {
          refetch();
          setMergeDialogOpen(false);
          toast(`Topic "${topic.topic}" merged successfully`);
        },
        onError: () => {
          try {
            // window.quack?.proactiveTrigger();
          } catch (e) {}
          toast.error("Failed to merge topic");
        },
      }
    );
  };

  return (
    <Table.Row className="hover:bg-gray-100">
      <Table.Cell>
        <Text>{topic.topic}</Text>
      </Table.Cell>
      <Table.Cell>
        <ExpandableText text={topic.description || "-"} />
      </Table.Cell>
      <Table.Cell>
        <Flex justify="end">
          <DropdownMenu
            items={[
              {
                label: "Edit",
                icon: <Pencil1Icon />,
                onClick: handleEdit,
              },
              {
                label: "Merge Topic",
                icon: <ArrowRightIcon />,
                onClick: handleMerge,
              },
            ]}
          >
            <DropdownMenu.Trigger>
              <Button
                variant="ghost"
                size="1"
                className="hover:bg-gray-100 data-[state=open]:bg-gray-100"
              >
                <DotsVerticalIcon className="text-gray-700" />
              </Button>
            </DropdownMenu.Trigger>
          </DropdownMenu>
        </Flex>

        {editDialogOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background rounded-lg p-6 w-full max-w-md">
              <Flex direction="column" gap="4">
                <Text size="5" weight="bold">
                  Edit Topic
                </Text>
                <Flex direction="column" gap="3">
                  <Flex direction="column" gap="2">
                    <Text size="2" weight="medium">
                      Topic Name *
                    </Text>
                    <Input
                      type="text"
                      value={editTopicName}
                      onChange={(e) => setEditTopicName(e.target.value)}
                      placeholder="Enter topic name"
                      className="w-full p-2"
                      autoFocus
                    />
                  </Flex>
                  <Flex direction="column" gap="2">
                    <Text size="2" weight="medium">
                      Description
                    </Text>
                    <Textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      className="min-h-[100px]"
                      placeholder="Enter topic description (optional)"
                    />
                  </Flex>
                </Flex>
                <Flex gap="3" justify="end">
                  <Button
                    variant="outline"
                    color="gray"
                    highContrast
                    onClick={() => setEditDialogOpen(false)}
                    disabled={isLoading}
                    className="text-gray-900 hover:bg-gray-100"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={!editTopicName.trim() || isLoading}
                    className="bg-black text-white hover:bg-black/90"
                  >
                    {isLoading ? (
                      <Loader2Icon className="animate-spin" />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </Flex>
              </Flex>
            </div>
          </div>
        )}

        {mergeDialogOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background rounded-lg p-6 w-full max-w-md">
              <Flex direction="column" gap="4">
                <Text size="5" weight="bold">
                  Merge Topic
                </Text>
                <Flex direction="column" gap="3">
                  <Text size="2" color="red" weight="medium">
                    <strong>WARNING:</strong> Merge "{topic.topic}" into another
                    topic. All instructions, prerequisites, and policies related
                    to "{topic.topic}" will be{" "}
                    <strong>permanently deleted</strong>, even for released
                    versions. <strong>This action cannot be undone.</strong>
                  </Text>
                  <Flex direction="column" gap="2">
                    <Text size="2" weight="medium">
                      Select destination topic *
                    </Text>
                    <SharedSelect
                      options={
                        allTopics
                          ?.filter((t: Topic) => t.id !== topic.id)
                          ?.sort((a: Topic, b: Topic) =>
                            a.topic.localeCompare(b.topic)
                          )
                          ?.map((t: Topic) => ({
                            label: t.topic,
                            value: t.id.toString(),
                          })) || []
                      }
                      value={selectedToTopicId}
                      onChange={setSelectedToTopicId}
                      placeholder="Choose topic to merge into"
                      classes={{ root: "w-full" }}
                    />
                  </Flex>
                </Flex>
                <Flex gap="3" justify="end">
                  <Button
                    variant="outline"
                    color="gray"
                    highContrast
                    onClick={() => setMergeDialogOpen(false)}
                    disabled={isMerging}
                    className="text-gray-900 hover:bg-gray-100"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleMergeConfirm}
                    disabled={
                      !selectedToTopicId ||
                      selectedToTopicId === topic.id.toString() ||
                      isMerging
                    }
                    className="bg-red-600 text-white hover:bg-red-700"
                  >
                    {isMerging ? (
                      <Loader2Icon className="animate-spin" />
                    ) : (
                      "Merge Topic"
                    )}
                  </Button>
                </Flex>
              </Flex>
            </div>
          </div>
        )}
      </Table.Cell>
    </Table.Row>
  );
}
